import { getMessaging, getToken, isSupported, onMessage, type Messaging } from 'firebase/messaging';
import { app } from '@/lib/firebase';
import { callAddFCMToken, callRemoveFCMToken } from '@/lib/callables';
import { toast } from '@/components/ui/Toast';

const TOKEN_STORAGE_KEY = 'makhe.fcmToken';
const SW_URL = '/firebase-messaging-sw.js';
const PUSH_DISMISS_KEY = 'makhe.pushPrompt.dismissed';

let messaging: Messaging | null = null;
let foregroundUnsub: (() => void) | null = null;

function pushEnabled(): boolean {
  return import.meta.env.VITE_USE_EMULATORS !== 'true';
}

function vapidKey(): string | undefined {
  const key = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  return typeof key === 'string' && key.length > 0 ? key : undefined;
}

async function getMessagingInstance(): Promise<Messaging | null> {
  if (!pushEnabled() || !(await isSupported())) return null;
  if (!messaging) messaging = getMessaging(app);
  return messaging;
}

async function serviceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register(SW_URL, { scope: '/' });
    await navigator.serviceWorker.ready;
    return reg;
  } catch {
    return null;
  }
}

/** True when this build + browser can do Web Push (permission may still be needed). */
export async function canUsePush(): Promise<boolean> {
  return pushEnabled() && Boolean(vapidKey()) && (await isSupported());
}

/** Register token when permission is already granted (no prompt). */
export async function registerPushIfPermitted(): Promise<void> {
  if (!pushEnabled() || Notification.permission !== 'granted') return;
  const key = vapidKey();
  if (!key) return;

  const msg = await getMessagingInstance();
  const reg = await serviceWorkerRegistration();
  if (!msg || !reg) return;

  try {
    const token = await getToken(msg, { vapidKey: key, serviceWorkerRegistration: reg });
    if (!token) return;

    const prev = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (prev && prev !== token) {
      await callRemoveFCMToken({ token: prev }).catch(() => {});
    }
    if (prev !== token) {
      await callAddFCMToken({ token });
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    }
  } catch {
    /* permission revoked, SW missing, or offline */
  }
}

/** Prompt for permission and register — call from a user gesture (button tap). */
export async function requestPushPermissionAndRegister(): Promise<'granted' | 'denied' | 'unsupported'> {
  if (!pushEnabled() || !(await isSupported())) return 'unsupported';
  if (!vapidKey()) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';

  await registerPushIfPermitted();
  return 'granted';
}

export async function unregisterPushNotifications(): Promise<void> {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token) {
    await callRemoveFCMToken({ token }).catch(() => {});
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }
}

/** Foreground messages — background ones are handled by the service worker. */
export function subscribeForegroundPushMessages(): () => void {
  if (foregroundUnsub) return foregroundUnsub;

  let cancelled = false;
  void (async () => {
    const msg = await getMessagingInstance();
    if (!msg || cancelled) return;
    const off = onMessage(msg, (payload) => {
      const title = payload.notification?.title ?? 'Makhe';
      const body = payload.notification?.body;
      toast.info(body ? `${title} — ${body}` : title);
    });
    if (cancelled) {
      off();
      return;
    }
    foregroundUnsub = off;
  })();

  const cleanup = () => {
    cancelled = true;
    foregroundUnsub?.();
    foregroundUnsub = null;
  };
  return cleanup;
}

export function pushPermissionState(): NotificationPermission | 'unsupported' {
  if (!pushEnabled() || !vapidKey() || typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

export function isPushPromptDismissed(): boolean {
  try {
    return localStorage.getItem(PUSH_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissPushPrompt(): void {
  try {
    localStorage.setItem(PUSH_DISMISS_KEY, '1');
  } catch {
    /* private browsing */
  }
}
